import React, { Component } from "react";
import { Table, Select, AutoComplete, Button, Tooltip } from "antd";
import {
  WarningFilled,
  CaretDownOutlined,
  CaretUpOutlined
} from "@ant-design/icons";
import moment from "moment";
import io from "socket.io-client";
import { subscribeSocket } from "../../utils/socketSubscribe";
import Fetch from "../../utils/fetch";
import { socketURL } from "../../config";

const { Option } = Select;

const STATUS_DOT_COLOR = {
  "On Trip": "#52c41a",
  Charging: "#1890ff",
  Offline: "#bfbfbf"
};

const StatusCell = ({ status }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: STATUS_DOT_COLOR[status] || "#bfbfbf",
        flexShrink: 0
      }}
    />
    <span>{status || "—"}</span>
  </span>
);

const DetailLabel = ({ children }) => (
  <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>
    {children}
  </div>
);
const DetailValue = ({ children }) => (
  <div
    style={{
      fontSize: 14,
      fontWeight: 600,
      color: "#262626",
      marginBottom: 12
    }}
  >
    {children === null || children === undefined ? "—" : children}
  </div>
);

const getAlertDetails = record => {
  if (!record.activeAlert) return "—";
  const parts = [];
  if (record.soc !== null && record.soc !== undefined && record.soc <= 30)
    parts.push(`Low SOC (${record.soc}%)`);
  if (
    record.voltage !== null &&
    record.voltage !== undefined &&
    record.voltage > 630
  )
    parts.push(`High Voltage (${record.voltage} V)`);
  if (
    record.maxCellTemp !== null &&
    record.maxCellTemp !== undefined &&
    record.maxCellTemp > 42
  )
    parts.push(`High Battery Temp (${record.maxCellTemp} C)`);
  if (
    record.motorTemp !== null &&
    record.motorTemp !== undefined &&
    record.motorTemp > 90
  )
    parts.push(`High Motor Temp (${record.motorTemp} C)`);
  return parts.length ? parts.join(", ") : "—";
};

const ExpandedDetail = ({ record }) => {
  const col1 = [
    {
      label: "Trip ID",
      value: record.tripId
    },
    {
      label: "Min Cell Temp",
      value: record.minCellTemp !== null ? `${record.minCellTemp} C` : "—"
    },
    {
      label: "Current",
      value: record.current !== null ? `${record.current} A` : "—"
    },
    {
      label: "Max Cell Voltage",
      value: record.maxCellVoltage !== "--" ? `${record.maxCellVoltage} V` : "—"
    }
  ];
  const col2 = [
    {
      label: "Trip Start SOC",
      value: record.tripStartSoc !== null ? `${record.tripStartSoc}%` : "—"
    },
    {
      label: "Max Cell Temp",
      value: record.maxCellTemp !== null ? `${record.maxCellTemp} C` : "—"
    },
    {
      label: "Power",
      value: record.instantPower !== null ? `${record.instantPower} kW` : "—"
    },
    {
      label: "Location",
      value: record.lat && record.lng ? `${record.lat}, ${record.lng}` : "—"
    }
  ];
  const col3 = [
    {
      label: "Trip Start Odo",
      value: record.tripStartOdo !== null ? `${record.tripStartOdo} km` : "—"
    },
    {
      label: "Motor Temp",
      value: record.motorTemp !== null ? `${record.motorTemp} C` : "—"
    },
    { label: "Charging Status", value: record.chargingStatus || "—" },
    {
      label: "Last BMS Update",
      value: record.lastBmsUpdate
        ? moment(record.lastBmsUpdate).format("HH:mm")
        : "—"
    }
  ];
  const col4 = [
    {
      label: "Range Remaining",
      value:
        record.rangeRemaining === "N/A"
          ? "N/A"
          : record.rangeRemaining !== null
          ? `${record.rangeRemaining} km`
          : "—"
    },
    {
      label: "Battery Voltage",
      value: record.voltage !== null ? `${record.voltage} V` : "—"
    },
    {
      label: "Min Cell Voltage",
      value: record.minCellVoltage !== "--" ? `${record.minCellVoltage} V` : "—"
    },
    { label: "Alert Details", value: getAlertDetails(record) }
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: "0 24px",
        padding: "16px 24px",
        background: "#fafafa",
        borderTop: "1px solid #f0f0f0"
      }}
    >
      {[col1, col2, col3, col4].map((col, ci) => (
        <div key={ci}>
          {col.map((item, ri) => (
            <div key={ri}>
              <DetailLabel>{item.label}</DetailLabel>
              <DetailValue>{item.value}</DetailValue>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

class EvLiveTrips extends Component {
  state = {
    loading: false,
    allTrips: [],
    trips: [],
    expandedRowKeys: [],
    busRc: "",
    status: undefined,
    oem: undefined,
    route: "",
    busOptions: [],
    serviceOptions: []
  };

  socket = null;

  componentDidMount() {
    // Load trips + filter options together on first load (all in parallel).
    Promise.all([this.fetchTrips(), this.loadOptions()]);
    // server accepts WebSocket only (no long-polling) - see server/socket.js
    this.socket = io(socketURL, {
      withCredentials: true,
      transports: ["websocket"]
    });
    subscribeSocket(this.socket, { channels: ["evLiveTrips"] });
    this.socket.on("evLiveTrips", this.handleSocketData);
  }

  loadOptions = async () => {
    try {
      const [buses, services] = await Promise.all([
        Fetch("/ev-trips/getBusRc", { method: "get" }),
        Fetch("/ev-trips/getService", { method: "get" })
      ]);
      this.setState({
        busOptions: Array.isArray(buses)
          ? buses.map(b => b.rc).filter(Boolean)
          : [],
        serviceOptions: Array.isArray(services)
          ? services.map(s => s.name).filter(Boolean)
          : []
      });
    } catch (err) {
      // non-fatal: fields still work as free text
    }
  };

  componentWillUnmount() {
    if (this.socket) this.socket.disconnect();
  }

  applyFilters = allTrips => {
    const { busRc, status, oem, route } = this.state;
    return allTrips
      .filter(
        t => !busRc || t.busRc.toLowerCase().includes(busRc.toLowerCase())
      )
      .filter(t => !status || t.status === status)
      .filter(t => !oem || t.oem === oem)
      .filter(
        t =>
          !route ||
          (t.serviceName &&
            t.serviceName.toLowerCase().includes(route.toLowerCase()))
      );
  };

  handleSocketData = data => {
    console.log("ev Data Recived");
    const allTrips = Array.isArray(data) ? data : [];
    this.setState({ allTrips, trips: this.applyFilters(allTrips) });
  };

  fetchTrips = async () => {
    this.setState({ loading: true });
    try {
      const response = await Fetch("/ev-trips/live", { method: "get" });
      const allTrips = Array.isArray(response) ? response : [];
      this.setState({
        allTrips,
        trips: this.applyFilters(allTrips),
        loading: false
      });
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
    }
  };

  toggleExpand = key => {
    this.setState(prev => {
      const { expandedRowKeys } = prev;
      if (expandedRowKeys.includes(key)) {
        return { expandedRowKeys: expandedRowKeys.filter(k => k !== key) };
      }
      return { expandedRowKeys: [...expandedRowKeys, key] };
    });
  };

  getColumns = () => [
    {
      title: "Bus RC",
      dataIndex: "busRc",
      key: "busRc",
      width: 130
    },
    {
      title: "Service Name",
      dataIndex: "serviceName",
      key: "serviceName",
      width: 220,
      render: val => val || <span style={{ color: "#bfbfbf" }}>—</span>
    },
    {
      title: "Trip Start Date",
      dataIndex: "tripStartTime",
      key: "tripStartTime",
      width: 220,
      render: val => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 500 }}>
              {moment(val).format("h:mm A")}
            </span>
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
              {moment(val).format("D MMM")}
            </span>
          </div>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: val => <StatusCell status={val} />
    },
    {
      title: "SOC%",
      dataIndex: "soc",
      key: "soc",
      width: 90,
      align: "center",
      render: (val, record) => {
        if (val === null || val === undefined)
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        const isAlert = record.activeAlert;
        return (
          <span
            style={{
              color: isAlert ? "#ff4d4f" : "inherit",
              fontWeight: isAlert ? 600 : 400
            }}
          >
            {val}
          </span>
        );
      }
    },
    {
      title: "ODO km",
      dataIndex: "odometer",
      key: "odometer",
      width: 110,
      align: "center",
      render: val =>
        val !== null && val !== undefined ? (
          Number(val).toLocaleString()
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        )
    },
    {
      title: "Speed",
      dataIndex: "speed",
      key: "speed",
      width: 90,
      align: "center",
      render: val =>
        val !== null && val !== undefined ? (
          val
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        )
    },
    {
      title: "Alert",
      dataIndex: "activeAlert",
      key: "activeAlert",
      width: 80,
      align: "center",
      render: val =>
        val ? (
          <Tooltip title="Active alert">
            <WarningFilled style={{ color: "#faad14", fontSize: 20 }} />
          </Tooltip>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        )
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => {
        const expanded = this.state.expandedRowKeys.includes(record.tripId);
        return (
          <Button
            size="small"
            style={{
              background: "#e8f0fe",
              border: "none",
              color: "#1a73e8",
              fontWeight: 500,
              borderRadius: 6,
              boxShadow: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4
            }}
            onClick={() => this.toggleExpand(record.tripId)}
          >
            {expanded ? "Hide" : "Details"}
            {expanded ? (
              <CaretUpOutlined style={{ fontSize: 10 }} />
            ) : (
              <CaretDownOutlined style={{ fontSize: 10 }} />
            )}
          </Button>
        );
      }
    }
  ];

  setFilter = (key, value) => {
    this.setState({ [key]: value }, () => {
      this.setState({ trips: this.applyFilters(this.state.allTrips) });
    });
  };

  render() {
    const {
      loading,
      trips,
      expandedRowKeys,
      busRc,
      status,
      oem,
      route,
      busOptions,
      serviceOptions
    } = this.state;

    return (
      <div style={{ padding: "0 16px" }}>
        {/* Filter bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 0 12px",
            flexWrap: "wrap"
          }}
        >
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#262626",
              marginRight: 8,
              whiteSpace: "nowrap"
            }}
          >
            Live Trips
          </span>

          <AutoComplete
            placeholder="Search by BUS RC"
            style={{ width: 180 }}
            value={busRc}
            options={busOptions.map(rc => ({ value: rc }))}
            filterOption={(input, option) =>
              option.value.toUpperCase().includes((input || "").toUpperCase())
            }
            onChange={v => this.setFilter("busRc", v || "")}
            allowClear
          />
          <Button
            type="primary"
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
            onClick={this.fetchTrips}
          >
            Search
          </Button>

          <Select
            allowClear
            placeholder="Choose Status"
            style={{ width: 150 }}
            value={status}
            onChange={v => this.setFilter("status", v)}
            suffixIcon={<CaretDownOutlined />}
          >
            <Option value="Not Charging">Not Charging</Option>
            <Option value="Charging">Charging</Option>
            <Option value="Offline">Offline</Option>
          </Select>

          <Select
            allowClear
            placeholder="All OEM"
            style={{ width: 120 }}
            value={oem}
            onChange={v => this.setFilter("oem", v)}
            suffixIcon={<CaretDownOutlined />}
          >
            <Option value="NAARNI">NAARNI</Option>
            <Option value="JBM">JBM</Option>
          </Select>

          <AutoComplete
            placeholder="Choose Route"
            style={{ width: 300 }}
            value={route || undefined}
            options={serviceOptions.map(name => ({ value: name }))}
            filterOption={(input, option) =>
              option.value.toLowerCase().includes((input || "").toLowerCase())
            }
            onChange={v => this.setFilter("route", v || "")}
            allowClear
          />

          <div style={{ marginLeft: "auto" }}>
            <Button
              style={{
                background: "#ede9fe",
                border: "none",
                color: "#7c3aed",
                fontWeight: 500,
                boxShadow: "none"
              }}
              onClick={this.fetchTrips}
            >
              Search / Refresh
            </Button>
          </div>
        </div>

        <Table
          columns={this.getColumns()}
          dataSource={trips}
          rowKey={r => r.tripId}
          loading={loading}
          pagination={false}
          scroll={{ y: "70vh" }}
          size="middle"
          rowClassName={() => "ev-trip-row"}
          expandedRowKeys={expandedRowKeys}
          expandedRowRender={record => <ExpandedDetail record={record} />}
          expandIcon={() => null}
          onExpand={() => {}}
        />
      </div>
    );
  }
}

export default EvLiveTrips;
